import React from "react";
import { css } from "glamor";
import { detectCardBrand } from "../../utils/cardFormatters";
import CardBrandIcon from "./CardBrandIcon";

function ChipIcon() {
  return (
    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="19" height="13" rx="2.5" fill="#D4AF37" stroke="#B8941F" strokeWidth="0.5" />
      <rect x="4" y="3" width="12" height="8" rx="1" fill="#B8941F" opacity="0.6" />
      <rect x="6" y="5" width="8" height="4" rx="0.5" fill="#D4AF37" opacity="0.8" />
      <line x1="10" y1="0.5" x2="10" y2="3" stroke="#B8941F" strokeWidth="1" />
      <line x1="10" y1="11" x2="10" y2="13.5" stroke="#B8941F" strokeWidth="1" />
      <line x1="0.5" y1="7" x2="4" y2="7" stroke="#B8941F" strokeWidth="1" />
      <line x1="16" y1="7" x2="19.5" y2="7" stroke="#B8941F" strokeWidth="1" />
    </svg>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const cardOuter = css({
  position: "relative",
  width: "100%",
  height: 190,
  perspective: "1000px",
});

const flipper = (flipped) => css({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  transformStyle: "preserve-3d",
  transition: "transform 0.4s ease",
  transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
});

const cardFace = css({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  borderRadius: 14,
  overflow: "hidden",
  WebkitBackfaceVisibility: "hidden",
  backfaceVisibility: "hidden",
  background: "linear-gradient(135deg, rgba(71, 85, 105, 1) 0%, rgba(71, 85, 105, 0.8) 100%)",
});

const cardBack = css({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  borderRadius: 14,
  overflow: "hidden",
  WebkitBackfaceVisibility: "hidden",
  backfaceVisibility: "hidden",
  background: "linear-gradient(135deg, rgba(71, 85, 105, 1) 0%, rgba(71, 85, 105, 0.8) 100%)",
  transform: "rotateY(180deg)",
});

// Front face styles

const orbStyle = css({
  position: "absolute",
  width: 160,
  height: 160,
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.05)",
  top: -53.33,
  left: 233.33,
  pointerEvents: "none",
});

const contentStyle = css({
  position: "absolute",
  top: 24,
  left: 24,
  width: 292,
});

const topRowStyle = css({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  height: 28,
});

const chipWrapper = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 28,
  background: "#D4AF37",
  borderRadius: 4,
});

const creditoText = css({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 600,
  fontSize: 11,
  lineHeight: "16.5px",
  letterSpacing: "0.0455em",
  textTransform: "uppercase",
  color: "rgba(255, 255, 255, 0.8)",
  margin: 0,
});

const numberRow = css({
  marginTop: 43,
  height: 27,
  display: "flex",
  alignItems: "center",
});

const numberText = css({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 500,
  fontSize: 18,
  lineHeight: "27px",
  letterSpacing: "0.1667em",
  color: "#FFFFFF",
  margin: 0,
});

const bottomRow = css({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "flex-end",
  marginTop: 12,
  height: 32,
});

const fieldColumn = css({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const fieldLabel = css({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 400,
  fontSize: 8,
  lineHeight: "12px",
  letterSpacing: "0.0625em",
  textTransform: "uppercase",
  color: "rgba(255, 255, 255, 0.5)",
  margin: 0,
});

const fieldValue = css({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 500,
  fontSize: 12,
  lineHeight: "18px",
  textTransform: "uppercase",
  color: "#FFFFFF",
  margin: 0,
});

// Back face styles

const magneticStripe = css({
  position: "absolute",
  top: 30,
  left: 0,
  width: "100%",
  height: 44,
  background: "#1E293B",
});

const signaturePanel = css({
  position: "absolute",
  top: 104,
  left: 16,
  right: 16,
  height: 36,
  background: "repeating-linear-gradient(90deg, #e2e8f0 0px, #e2e8f0 6px, #f8fafc 6px, #f8fafc 12px)",
  borderRadius: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  paddingRight: 8,
  gap: 4,
});

const cvvLabelStyle = css({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 400,
  fontSize: 8,
  letterSpacing: "0.0625em",
  textTransform: "uppercase",
  color: "#64748B",
  margin: 0,
});

const cvvValueStyle = css({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0F172A",
  letterSpacing: "0.15em",
  margin: 0,
  background: "#FFFFFF",
  padding: "2px 8px",
  borderRadius: 2,
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCardNumber(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "•••• •••• •••• ••••";
  return digits
    .padEnd(16, "•")
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CardPreview({ cardNumber, holderName, goodThru, cvv, flipped }) {
  const displayNumber = formatCardNumber(cardNumber);
  const displayName = holderName ? holderName.toUpperCase() : "SEU NOME";
  const displayValidity = goodThru || "MM/AA";
  const displayCvv = cvv || "•••";
  const brand = detectCardBrand(cardNumber);

  return (
    // ph-no-capture: this preview renders the card number, holder, expiry and
    // CVV as plain text (not <input>s), so PostHog's input masking misses it.
    // The class blanks the whole card out of session replay (PCI/LGPD). Same
    // rule as the checkout preview — do not remove.
    <div className={`${cardOuter} ph-no-capture`}>
      <div className={flipper(flipped)}>

        {/* ── Front ── */}
        <div className={cardFace}>
          <div className={orbStyle} />
          <div className={contentStyle}>
            <div className={topRowStyle}>
              <div className={chipWrapper}><ChipIcon /></div>
              {brand
              ? <CardBrandIcon brand={brand} size={42} />
              : <p className={creditoText}>Crédito</p>
            }
            </div>
            <div className={numberRow}>
              <p className={numberText}>{displayNumber}</p>
            </div>
            <div className={bottomRow}>
              <div className={fieldColumn}>
                <p className={fieldLabel}>Nome no cartão</p>
                <p className={fieldValue}>{displayName}</p>
              </div>
              <div className={fieldColumn} style={{ alignItems: "flex-end" }}>
                <p className={fieldLabel}>Validade</p>
                <p className={fieldValue}>{displayValidity}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Back ── */}
        <div className={cardBack}>
          <div className={magneticStripe} />
          <div className={signaturePanel}>
            <p className={cvvLabelStyle}>CVV</p>
            <p className={cvvValueStyle}>{displayCvv}</p>
          </div>
        </div>

      </div>
    </div>
  );
}
